import React, {
    PropsWithChildren,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ProfileSwitcherRef } from "../components/ProfileSwitcher";
import graphqlConfig from "../configs/graphql.config";
import analytics from "../contexts/FirebaseAnalytics";
import {
    ActivitiesInfo,
    ClientProfile,
    ProfileType,
    User,
    WorkerProfile,
} from "../__generated__/graphql";
import { useLazyQuery } from "@apollo/client";
import { GET_ALL_PROFILES_ACTIVITIES_INFO } from "../graphql/activity";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { AuthManagerContext } from "./AuthManager";
import {
    getWorkerProfileIsComplete,
    useGetIsWorkerProfileComplete,
} from "../hooks/use-get-is-worker-profile-complete";
import { useApolloErrorSnackbar, useSnackbar } from "../hooks/snackbar";
import { useI18n } from "../hooks/i18n-hooks";
import queryString from "query-string";
import { Platform } from "react-native";

export const KEYSTORE_DEFAULT_PROFILE_ID = "user.defaultProfileId";

export type ProfileIdType = `client:${string}` | `worker:${string}`;

type UnreadProfilesActivitiesType = Record<string, number>;

type ProfileSwitcherObserverListener = (
    profile: ClientProfile | WorkerProfile | null,
) => Promise<void> | void;

class ProfileSwitcherObserver {
    listeners: ProfileSwitcherObserverListener[] = [];
    constructor() {}

    registerProfileChangeListener = (listener: ProfileSwitcherObserverListener) => {
        const _func = async (profile: ClientProfile | WorkerProfile | null) => {
            await listener(profile);
        };
        this.listeners.push(_func);
        return () => {
            this.listeners = this.listeners.filter((listener) => listener !== _func);
        };
    };

    notifyProfileChanged = async (profile: ClientProfile | WorkerProfile | null) => {
        for (const listener of this.listeners) {
            await listener(profile);
        }
    };
}

export const buildFullProfileId = (profile: ClientProfile | WorkerProfile) => {
    // @ts-expect-error __typename exists in object but not in type
    if (profile.__typename === "WorkerProfile") {
        return { fullProfileId: `worker:${profile.id}` as ProfileIdType, profileType: "worker" };
        // @ts-expect-error __typename exists in object but not in type
    } else if (profile.__typename === "ClientProfile") {
        return { fullProfileId: `client:${profile.id}` as ProfileIdType, profileType: "client" };
    } else {
        throw new Error("Invalid profile object");
    }
};

export const findProfileForUser = (fullProfileId: string, user: User) => {
    if (fullProfileId.includes(":")) {
        const split = fullProfileId.split(":");
        const profileType = split[0];
        const profileId = split[1];
        if (profileType === "worker") {
            return user.workerProfile?.id === profileId ? user.workerProfile : null;
        } else if (profileType === "client") {
            return (
                user.clientProfiles?.find((clientProfile) => clientProfile.id === profileId) || null
            );
        } else {
            return null;
        }
    } else if (fullProfileId.length > 1) {
        if (user.workerProfile?.id === fullProfileId) {
            return user.workerProfile;
        }
        return (
            user.clientProfiles?.find((clientProfile) => clientProfile.id === fullProfileId) || null
        );
    }
    return null;
};

export const getDefaultProfileForUser = (user: User) => {
    if (user.workerProfile && getWorkerProfileIsComplete(user).isValid) {
        // priority to the completed talent
        return user.workerProfile;
    }
    if (user.clientProfiles?.length) {
        // then take the first client profile
        return user.clientProfiles[0];
    }
    if (user.workerProfile) {
        // return non valid talent profile in last resort
        return user.workerProfile;
    }
    return null;
};

interface ProfileSwitcherContextInterface {
    openProfileSwitch: () => void;
    /**
     * client, worker
     */
    profileType?: ProfileType;
    /**
     * UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
     */
    profileId?: string;
    /**
     * complete LiftProfile (profileType:UUID)
     */
    liftProfile?: string;
    /**
     * set profileId in form of `[worker|client]:[uuid]`
     */
    setProfile: (value: ClientProfile | WorkerProfile | null) => Promise<void> | void;
    setRef: (value: ProfileSwitcherRef) => void;
    refreshUnreadProfilesActivities: () => Promise<void>;
    unreadProfilesActivities: UnreadProfilesActivitiesType;
    profileIds: string[];
    observer: ProfileSwitcherObserver;
    switchingProfile: boolean;
}

export const ProfileSwitcherManagerContext = React.createContext<ProfileSwitcherContextInterface>({
    openProfileSwitch: () => {},
    setProfile: () => {},
    switchingProfile: false,
    setRef: () => {},
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    refreshUnreadProfilesActivities: () => {},
    unreadProfilesActivities: {},
    profileIds: [],
    observer: new ProfileSwitcherObserver(),
});

const ProfileSwitcherManager: React.FC<PropsWithChildren> = ({ children }) => {
    const observer = useMemo(() => new ProfileSwitcherObserver(), []);
    const i18n = useI18n();
    const { showSnackbar } = useSnackbar();
    const profileSwitcherRef = useRef<ProfileSwitcherRef | null>(null);
    const [profileId, setProfileId] = useState<ProfileIdType>();
    const [switchingProfile, setSwitchingProfile] = useState(false);
    const [unreadProfilesActivities, setUnreadProfilesActivities] =
        useState<UnreadProfilesActivitiesType>({});

    const { user, addRefreshListener } = useContext(AuthManagerContext);
    // we use lazy query here as using useQuery with skip=true is not working
    // as refetching with skip = true does not update data
    const [getActivities, { data, error }] = useLazyQuery(GET_ALL_PROFILES_ACTIVITIES_INFO);
    useApolloErrorSnackbar(error);
    const getIsWorkerProfileComplete = useGetIsWorkerProfileComplete();

    const [profileType, id] = useMemo(() => {
        if (profileId) {
            return profileId.split(":") as [ProfileType, string];
        } else {
            return [undefined, undefined];
        }
    }, [profileId]);

    const _setProfile = useCallback(
        async (newProfile: ClientProfile | WorkerProfile | null) => {
            if (!newProfile) {
                if (profileId) {
                    await AsyncStorage.removeItem(KEYSTORE_DEFAULT_PROFILE_ID);
                }
                setProfileId(undefined);
                await observer.notifyProfileChanged(null);
                return;
            }
            const { fullProfileId, profileType } = buildFullProfileId(newProfile);
            const isEffectiveSwitching = !!profileId; // if profileId is null, this is the initialization
            if (profileId === fullProfileId) {
                return;
            }
            try {
                setSwitchingProfile(true);
                if (fullProfileId) {
                    graphqlConfig.defaultContext.liftProfile = fullProfileId;
                    //be sure we reset cache once UI has been redrawn
                    //await graphqlConfig.cache.reset();
                    await AsyncStorage.setItem(KEYSTORE_DEFAULT_PROFILE_ID, fullProfileId);
                    setProfileId(fullProfileId);
                    switch (profileType) {
                        case "client":
                            await analytics().setUserProperties({ profileType: "client" });
                            break;
                        case "worker":
                            await analytics().setUserProperties({ profileType: "worker" });
                            break;
                    }
                    if (isEffectiveSwitching) {
                        // we only refetch all queries if it is an effective switching to avoid double fetch user at load time
                        await graphqlConfig.refetchQueries({ include: "active" });
                    }
                    // handle listeners
                    await observer.notifyProfileChanged(newProfile);
                    // notify user
                    const [fullProfileType, profileName] = (() => {
                        // @ts-expect-error __typename exists in object but not in type
                        switch (newProfile.__typename) {
                            case "ClientProfile":
                                if ((newProfile as ClientProfile).isPrivateClient) {
                                    return ["private_client", ""];
                                }
                                return ["client", (newProfile as ClientProfile).client.name];
                            case "WorkerProfile":
                                return ["worker", ""];
                            default:
                                throw new Error("Invalid profile type");
                        }
                    })();
                    if (isEffectiveSwitching) {
                        // do not notify if it is the first profile ( app is booting or first signup )
                        showSnackbar({
                            text: i18n.t(`profile_switch.on_change.${fullProfileType}`, {
                                name: profileName,
                            }),
                            mode: "info",
                        });
                    }
                }
            } finally {
                setSwitchingProfile(false);
            }
        },
        [i18n, observer, profileId, showSnackbar],
    );

    const openProfileSwitch = useCallback(() => {
        profileSwitcherRef.current?.open();
    }, []);

    const setRef = useCallback((newRef: RefObject<ProfileSwitcherRef>["current"]) => {
        profileSwitcherRef.current = newRef;
    }, []);

    const refreshUnreadProfilesActivities = useCallback(async () => {
        await getActivities();
    }, [getActivities]);

    useEffect(() => {
        // transform list of activities into a map
        const allProfilesActivitiesInfo = data?.getAllProfilesActivitiesInfo;
        if (!allProfilesActivitiesInfo) {
            setUnreadProfilesActivities({});
        } else {
            const _unreadProfilesActivities = (
                (data?.getAllProfilesActivitiesInfo || []) as ActivitiesInfo[]
            ).reduce(
                (result, value) => ({
                    ...result,
                    [value.profileId]: value.unreadTodos,
                }),
                {} as UnreadProfilesActivitiesType,
            );
            setUnreadProfilesActivities(_unreadProfilesActivities);
        }
    }, [data]);

    useEffect(() => {
        const unsubscribe = addRefreshListener(refreshUnreadProfilesActivities);

        return unsubscribe;
    }, [refreshUnreadProfilesActivities, addRefreshListener]);

    const liftProfile = useMemo<ProfileIdType | undefined>(
        () => (profileType && id ? `${profileType}:${id}` : undefined),
        [profileType, id],
    );

    useEffect(() => {
        void (async () => {
            // user may be null when the app start and the user not yet loaded by AuthManager
            if (user) {
                // profile_id param is not automatically handled by react-navigation on web
                if (Platform.OS === "web") {
                    const { query } = queryString.parseUrl(location.href);
                    const { profile_id } = query as { profile_id?: string };

                    if (profile_id) {
                        // Check if profileId exist across all the user's profiles
                        const profile = findProfileForUser(profile_id, user);
                        if (profile) {
                            await _setProfile(profile);
                            return;
                        }
                    }
                }

                const storedProfileId = await AsyncStorage.getItem(KEYSTORE_DEFAULT_PROFILE_ID);

                if (storedProfileId) {
                    // Has profile id stored in local storage

                    // Check if profileId exist across all the user's profiles
                    const profile = findProfileForUser(storedProfileId, user);
                    if (profile) {
                        await _setProfile(profile);
                        return;
                    }
                }
                const defaultProfile = getDefaultProfileForUser(user);
                await _setProfile(defaultProfile);
            } else {
                // Set profile to null if user is not defined (initial load or logout).
                // Correct profile with be set when user is defined again (code above)
                await _setProfile(null);
            }
        })();
    }, [_setProfile, getIsWorkerProfileComplete, user]);

    const profileIds = useMemo(
        () => [
            ...(user?.workerProfile ? [`worker:${user.workerProfile.id}`] : []),
            ...(user?.clientProfiles || []).map((clientProfile) => `client:${clientProfile.id}`),
        ],
        [user?.clientProfiles, user?.workerProfile],
    );
    return (
        <ProfileSwitcherManagerContext.Provider
            value={{
                openProfileSwitch,
                profileType,
                profileId: id,
                liftProfile,
                setProfile: _setProfile,
                setRef,
                refreshUnreadProfilesActivities,
                unreadProfilesActivities,
                profileIds,
                observer,
                switchingProfile,
            }}
        >
            {children}
        </ProfileSwitcherManagerContext.Provider>
    );
};

type MockedProfileSwitcherManagerProps = {
    openProfileSwitch?: () => void;
    profileType?: ProfileType;
    profileId?: string;
    setProfile?: (value: ClientProfile | WorkerProfile | null) => void;
    setRef?: (value: ProfileSwitcherRef) => void;
    profileIds?: string[];
    observer?: ProfileSwitcherObserver;
    switchingProfile?: boolean;
};

/**
 * User for Storybook and Testing
 */
export const MockedProfileSwitcherManager: React.FC<
    PropsWithChildren<MockedProfileSwitcherManagerProps>
> = ({
    children,
    openProfileSwitch = () => {},
    profileType,
    profileId,
    setProfile = () => {},
    setRef = () => {},
    profileIds = [],
    switchingProfile = false,
}) => {
    const observer = useMemo(() => new ProfileSwitcherObserver(), []);
    return (
        <ProfileSwitcherManagerContext.Provider
            value={{
                openProfileSwitch,
                profileType,
                profileId,
                setProfile,
                setRef,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                refreshUnreadProfilesActivities: () => {},
                unreadProfilesActivities: {},
                profileIds,
                observer,
                switchingProfile,
            }}
        >
            {children}
        </ProfileSwitcherManagerContext.Provider>
    );
};

export default ProfileSwitcherManager;
